/*
 * Size every autocomplete dropdown to the space it actually has.
 *
 * WHY THIS EXISTS
 * The menus were capped in CSS at a fixed fraction of the viewport. A fraction
 * cannot know where the field sits, so on a short window a field near the top
 * got a menu sized for the small gap BELOW it while far more room sat unused
 * above. Only the browser knows the real answer, and only once Popper has
 * placed the menu — so we measure after placement.
 *
 * THE ORDERING TRAP (this is the subtle part)
 * Options load asynchronously: the menu opens holding a single "No Data" row,
 * Popper places it, then the server responds and the list grows. Popper does
 * not reconsider placement on its own, so the components dispatch a `resize`
 * to make it re-run flip.
 *
 * But an inline max-height from this module breaks that: Popper re-measures a
 * menu we have already shrunk to fit below, concludes it fits, and keeps the
 * bad placement. The constraint quietly prevented the very flip it depended on.
 *
 * So on resize we CLEAR every inline max-height synchronously, before Popper's
 * own resize handler runs, letting it decide against the menu's natural height.
 * Ours fires first because it is registered at app start and Popper's only when
 * a menu mounts, and listeners on the same target run in registration order.
 * We then re-measure a frame later, once Popper has moved things.
 *
 * A content observer does the same when the option list itself changes, which
 * covers paths that repopulate without a resize.
 */

const ROW = 32;        // one option: 18px line-height + 2 x 7px padding
const CHROME = 8;      // menu's own 4px top/bottom padding
const GAP = 10;        // breathing room against the window edge
const MIN_H = ROW * 2 + CHROME;   // never collapse below two options
const MAX_H = 488;                // 15 rows; matches the CSS safety net

function clearMenuHeights() {
  const menus = document.querySelectorAll(".rbt-menu");
  for (let i = 0; i < menus.length; i++) menus[i].style.maxHeight = "";
}

function fitMenu(menu) {
  if (!menu || !menu.getBoundingClientRect) return;
  try {
    // Measure the natural position, not the last height we forced on it.
    menu.style.maxHeight = "";

    const rect = menu.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;

    // Popper records which way it placed the menu.
    const placement = menu.getAttribute("data-placement") || "";
    const opensUp = placement.indexOf("top") === 0;

    const available = opensUp
      ? rect.bottom - GAP            // room between the field and the top
      : viewportH - rect.top - GAP;  // room between the field and the bottom

    // Whole rows only — a partial row at the cut looks like a rendering fault.
    let h = Math.floor((available - CHROME) / ROW) * ROW + CHROME;
    if (h < MIN_H) h = MIN_H;
    if (h > MAX_H) h = MAX_H;

    // Only constrain when the content genuinely exceeds the space. Setting a
    // max-height on a menu that already fits gives Popper a reason to think it
    // fits somewhere it does not, which is how the bad placement above stuck.
    if (menu.scrollHeight > h) {
      menu.style.maxHeight = h + "px";
    }
  } catch (e) {
    /* Leave the CSS cap in place if anything here fails — a mis-sized menu is
       a far smaller problem than a thrown error inside an observer. */
  }
}

function fitAllOpenMenus() {
  const menus = document.querySelectorAll(".rbt-menu");
  for (let i = 0; i < menus.length; i++) fitMenu(menus[i]);
}

/* Re-fit when a menu's own contents change (options arriving from the server).
   Clearing first, then measuring a frame later, gives Popper an unconstrained
   menu to re-place before we size it. */
function watchMenuContent(menu) {
  if (!menu || menu.__bmContentWatched) return;
  menu.__bmContentWatched = true;
  try {
    const mo = new MutationObserver(() => {
      menu.style.maxHeight = "";
      window.requestAnimationFrame(() => fitMenu(menu));
    });
    mo.observe(menu, { childList: true, subtree: true });
  } catch (e) {
    /* content watching is an optimisation; the resize path still covers it */
  }
}

export default function installDropdownFit() {
  if (typeof window === "undefined" || typeof MutationObserver === "undefined") return;
  if (window.__bmDropdownFitInstalled) return;   // survive hot reloads
  window.__bmDropdownFitInstalled = true;

  const handle = (node) => {
    if (!node || node.nodeType !== 1) return;
    let menu = null;
    if (node.classList && node.classList.contains("rbt-menu")) menu = node;
    else if (node.querySelector) menu = node.querySelector(".rbt-menu");
    if (!menu) return;
    watchMenuContent(menu);
    window.requestAnimationFrame(() => fitMenu(menu));
  };

  const observer = new MutationObserver((records) => {
    for (let r = 0; r < records.length; r++) {
      const added = records[r].addedNodes;
      for (let i = 0; i < added.length; i++) handle(added[i]);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Registered at app start, so this runs BEFORE Popper's own resize handler.
  // Clearing synchronously here is what lets Popper re-run flip against the
  // natural height; we then re-measure once it has finished moving things.
  const onResize = () => {
    clearMenuHeights();
    window.requestAnimationFrame(fitAllOpenMenus);
  };

  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("scroll", onResize, { passive: true, capture: true });
}
